import {
  ActivityStatus,
  ConfirmDialog,
  useMediaMobileScreen,
} from "@cca/components";
import CoursePlayerMenu from "@core/componentsV2/PlayerV2";
import { Feature } from "@core/enums";
import { DayOutletContextV2 } from "@core/typesV2";
import SecuredPageLayout from "@layouts/securedPage";
import { getCourseDays } from "@services";
import { useQueryClient } from "@tanstack/react-query";
import { useFlag } from "@unleash/proxy-client-react";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import {
  useCourseOutlineProvider,
  useCourseProvider,
} from "../CourseHomePageV2/providers";
import { useCourseItemActivitiesProvider, useStartActivity } from "./providers";
import {
  areActivitiesNotStarted,
  areActivitiesWithProgress,
  findDayPath,
} from "./utils";

export enum NotificationState {
  Open = "open",
  Acknowledged = "acknowledged",
}

export const CourseDayPage = () => {
  const { tab, courseId, dayId, activityId } = useParams<{
    tab: string;
    courseId: string;
    dayId: string;
    activityId: string;
    learnerId?: string;
  }>();

  const [notificationStates, setNotificationState] = useState<
    Record<string, NotificationState>
  >({});

  const { data: outlines } = useCourseOutlineProvider({ courseId });

  const dayIds = useMemo(
    () => getCourseDays(outlines || []).map((courseDay) => courseDay.id),
    [outlines],
  );
  const { unit, topic, day } =
    (outlines && dayId ? findDayPath(outlines, dayId) : {}) || {};

  const {
    data: activities,
    isLoading,
    isFetched: isActivitiesFetched,
    refetch: refetchActivities,
  } = useCourseItemActivitiesProvider({
    courseId,
    dayId,
  });

  const [syncedActivityId, setSyncedActivityId] = useState<string>();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!activityId) {
      setSyncedActivityId(undefined);
      return undefined;
    }

    let isCurrentActivity = true;

    const syncActivities = async () => {
      const { data: syncedActivities } = await refetchActivities();
      const syncedActivity = syncedActivities?.find(
        (activity) => activity.activityId === activityId,
      );

      if (
        syncedActivity?.progress?.status === ActivityStatus.NotStarted &&
        syncedActivity?.progress?.isReset
      ) {
        await queryClient.refetchQueries({
          queryKey: [
            "assets",
            "progress",
            { courseId, activityId, learnerId: null },
          ],
          exact: true,
          type: "all",
        });
      }

      if (isCurrentActivity) {
        setSyncedActivityId(activityId);
      }
    };

    void syncActivities();

    return () => {
      isCurrentActivity = false;
    };
  }, [activityId, courseId, queryClient, refetchActivities]);

  const isActivitySynced =
    activityId === undefined || syncedActivityId === activityId;

  const isUser = useFlag(Feature.CourseManagementLearn);
  const { data: course } = useCourseProvider();
  const navigate = useNavigate();
  const isMobile = useMediaMobileScreen();
  const isOpenDaynMobile = isMobile && activityId != undefined;

  useEffect(() => {
    if (activityId !== undefined || isMobile || !activities) return;

    const firstActivity = activities[0];

    if (!firstActivity) return;

    const activityInProgress = activities.find(
      (activity) => activity?.progress?.status === ActivityStatus.InProgress,
    );

    const redirectDayItem = activityInProgress || firstActivity;

    navigate(`activities/${redirectDayItem.activityId.toString()}`);
  }, [navigate, activities, activityId, isMobile]);

  const { mutate: startActivity } = useStartActivity(courseId, dayId);

  useEffect(() => {
    if (!isActivitiesFetched || !isMobile) return;

    if (
      activities?.length &&
      areActivitiesWithProgress(activities) &&
      areActivitiesNotStarted(activities)
    ) {
      const firstActivity = activities[0];

      startActivity({
        activityId: firstActivity.activityId,
      });
    }
  }, [isActivitiesFetched, isMobile, activities, startActivity]);

  const hasActivities = activities ? activities?.length > 0 : false;

  const courseLink = `/courses-v2/${tab}/${courseId}`;
  const courseHomeLink = `${courseLink}/home`;
  const courseProgressLink = `${courseLink}/${!isUser ? "outline" : "progress"}`;
  const dayIndex = dayId ? dayIds.indexOf(dayId) : -1;
  const nextDayId = dayIds[dayIndex + 1];
  const nextDayLink = nextDayId
    ? `/courses-v2/${tab}/${courseId}/${!isUser ? "outline" : "progress"}/days/${nextDayId}`
    : courseHomeLink;

  const lastDayInCourse = dayIndex === dayIds.length - 1;
  const showCongratulations =
    nextDayLink === nextDayLink &&
    course &&
    "progress" in course &&
    !course.progress?.submittedOn &&
    isUser &&
    lastDayInCourse;

  const openNotificationModal = (id: string) => {
    setNotificationState((prev) => {
      if (prev[id] === NotificationState.Acknowledged) {
        return prev;
      }
      return { ...prev, [id]: NotificationState.Open };
    });
  };

  const closeNotificationModal = (id: string) => {
    setNotificationState((prev) => ({
      ...prev,
      [id]: NotificationState.Acknowledged,
    }));
  };

  const outletContext = {
    activities: activities || [],
    courseIsGradable: course?.isGradable || false,
    isActivitiesFetched,
    nextDayLink,
    dayIds,
    dayIndex,
    notificationStates,
    closeNotificationModal,
    openNotificationModal,
  } satisfies DayOutletContextV2;

  return (
    <SecuredPageLayout
      features={undefined}
      routes={[
        {
          path: "/courses-v2",
          breadcrumb: null,
        },
        {
          path: "/courses-v2/:courseId",
          breadcrumb: null,
        },
        {
          path: "/courses-v2/:tab/:courseId",
          breadcrumb: null,
        },
        {
          path: "/courses-v2/:tab/:courseId/:subtab",
          breadcrumb: course?.name || "",
        },
        {
          path: "/courses-v2/:tab/:courseId/:subtab/days",
          breadcrumb: null,
        },
        {
          path: "/courses-v2/:tab/:courseId/:subtab/days/:dayId",
          breadcrumb: null,
        },
        {
          path: "/courses-v2/:tab/:courseId/:subtab/days/:dayId/activities",
          breadcrumb: null,
        },
        {
          path: "/courses-v2/:tab/:courseId/:subtab/days/:dayId/activities/:activityId",
          breadcrumb: day?.name || "",
        },
      ]}
      title={day?.name}
    >
      <CoursePlayerMenu
        activities={activities ?? []}
        day={day}
        hideMenu={isOpenDaynMobile}
        isLoading={isLoading}
        progress={isUser}
        topic={topic}
        unit={unit}
      >
        {isActivitySynced && <Outlet context={outletContext} />}
        {!hasActivities && isActivitiesFetched && (
          <ConfirmDialog
            confirmBtnText="Next"
            hasCloseButton={false}
            onCancel={() => navigate(courseProgressLink)}
            onConfirm={() =>
              navigate(nextDayLink, {
                state: {
                  showCongratulations: showCongratulations,
                },
              })
            }
            show={true}
            size="sm"
            title="No content available"
          >
            No content available in this item.
          </ConfirmDialog>
        )}
      </CoursePlayerMenu>
    </SecuredPageLayout>
  );
};
